import React from 'react';
import { RefreshTimestamp } from './ListViewRefresh/ListViewRefresh.style';
import { parseListViewDateTime } from '../../libs/listViewPresentation';
import type { TimeFormatSetting } from '../types';

interface ListViewRefreshStatusProps {
  /* A refresh is on its way: the time shown is the previous one's, dimmed
     until the new one replaces it in place */
  isRefreshing: boolean;
  /* Whether the last refresh attempt failed; the stale rows stay on screen, so
     this is the only sign of it */
  failed: boolean;
  lastRefreshedAt: string | null;
  timeFormatSetting: TimeFormatSetting;
}

/* When the shown bookings were last refreshed, as a quiet line left of the
 * refresh button. It says the same thing at every width — only how much of it
 * is spelled out changes, so the line never wraps and never resizes. */
const ListViewRefreshStatus = ({
  isRefreshing,
  failed,
  lastRefreshedAt,
  timeFormatSetting
}: ListViewRefreshStatusProps) => {
  if (failed)
    return (
      <RefreshTimestamp
        $dimmed={false}
        tw="text-red-darken-10"
        data-test-refresh-failed
      >
        {i18n('Refresh failed')}
      </RefreshTimestamp>
    );
  /* lastRefreshedAt is a UTC ISO string (and parseListViewDateTime keeps the
     offset it was given), so convert to the browser's local time for display */
  const parsedLastRefreshed = parseListViewDateTime(lastRefreshedAt);
  if (!parsedLastRefreshed) return null;
  const time = parsedLastRefreshed.local().format(timeFormatSetting);
  return (
    <RefreshTimestamp
      $dimmed={isRefreshing}
      $color="gray light"
      data-test-refresh-status
    >
      {/* Both are rendered and one is hidden per breakpoint: the phrase and
          the bare time are separate sentences to translate, and swapping them
          in CSS keeps the two out of each other's way */}
      <span tw="hidden md:inline">{i18n('last updated {time}', { time })}</span>
      <span tw="md:hidden">{time}</span>
    </RefreshTimestamp>
  );
};

export default ListViewRefreshStatus;
